import { useCMSPageField } from '@/cms/fields'
import { Custom500 } from '@/pages/500'
import { Basket } from '@/scopes/checkout/components/Basket'
import { CheckoutBenefits } from '@/scopes/checkout/components/CheckoutBenefits'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError'
import {
  CheckoutHeading,
  CheckoutHeadingDescription,
  CheckoutHeadingTitle,
} from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutNextStep } from '@/scopes/checkout/components/CheckoutNextStep'
import { CheckoutPreviousStep } from '@/scopes/checkout/components/CheckoutPreviousStep'
import { CheckoutProgress } from '@/scopes/checkout/components/CheckoutProgress'
import { CheckoutUsps } from '@/scopes/checkout/components/CheckoutUsps'
import { GenerateSIDButton } from '@/scopes/checkout/components/GenerateSIDButton'
import {
  Content,
  Loader,
  MainContent,
  PageLayout,
  SideBarContent,
  Wrapper,
} from '@/scopes/checkout/components/Layout'
import { Services } from '@/scopes/checkout/components/services'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { CheckoutContextProvider } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQuery, useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import { useUpdateLoginButtonLinks } from '@/scopes/checkout/pages/helpers/RedirectLinks'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { Header } from '../../common/components/Header'

const queryClient = new QueryClient()

function Page() {
  const { isLoading, data } = useBookingDraftQuery()
  const intl = useIntl()
  const sid = useCheckoutSid()

  useSendCheckoutEvent()
  useUpdateLoginButtonLinks()

  if (!sid)
    return <GenerateSIDButton />

  return (
    <PageLayout>
      {data?.error && data.error.hasError && <CheckoutError />}
      {(!data?.error || (data?.error && !data.error.hasError)) && (
        <>
          <Header hideLiveSearch hideNavItems />
          <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand="sunweb" />}>
            <Wrapper>
              {isLoading
                ? (
                    <Loader />
                  )
                : (
                    <>
                      <CheckoutPreviousStep />
                      <CheckoutProgress />
                      <Content>
                        <MainContent>
                          <CheckoutHeading direction="y">
                            <CheckoutHeadingTitle>
                              {intl.formatMessage({ id: 'checkout_steps_header_step_1_title' })}
                            </CheckoutHeadingTitle>
                            <CheckoutHeadingDescription>
                              {intl.formatMessage({ id: 'checkout_steps_header_step_1_description' })}
                            </CheckoutHeadingDescription>
                          </CheckoutHeading>
                          <Services />
                          <CheckoutNextStep />
                          <CheckoutUsps />
                        </MainContent>
                        <SideBarContent>
                          <Basket />
                          <CheckoutBenefits />
                        </SideBarContent>
                      </Content>
                    </>
                  )}
            </Wrapper>
          </NewRelicErrorBoundary>
        </>
      )}
    </PageLayout>
  )
}

export default function CheckoutPageStep1() {
  const contextItemId = useCMSPageField('contextItemId')

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutContextProvider
        value={{
          contextItemId,
        }}
      >
        <Page />
        <DevTools />
      </CheckoutContextProvider>
    </QueryClientProvider>
  )
}
